import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { Card, ListGroup, ListGroupItem, Button, ButtonToolbar, InputGroup, Form, OverlayTrigger, Tooltip } from "react-bootstrap";
import Select from "react-select";
import { toAbsoluteUrl } from "../../../../_metronic/_helpers";
import { CostWithDiscount } from "./CostWithDiscount";
import { DivisionChip } from "./DivisionChip";
import { TruncatedTextWithTooltip } from "./TruncatedTextWithTooltip";
import { getDiscountFinalValue } from "../utils/shopGridDiscounts";
import { useShopGridContext } from "../context/ShopGridContext";

export const ShopProductCard = React.memo(function ShopProductCard({
  element,
  divisionClassName,
}) {
  const {
    displayName,
    maxDescriptionLength,
    imageHeight,
    getCartValue,
    setCartValue,
    richiediDatiVendita,
    discountsLoading,
    getScontoFinalValue,
    getScontoValue,
    onDiscountChange,
    getDiscountOptionsForElement,
    isDiscountInvalidForProductId,
  } = useShopGridContext();

  const imageUrl = element.imageUrl || "";
  const quantityInCart = getCartValue(element.id);
  const hasInvalidDiscount = isDiscountInvalidForProductId(element.id);

  const imageStyle = useMemo(() => {
    if (!imageUrl) return null;
    const resolved = imageUrl.startsWith("/") ? toAbsoluteUrl(imageUrl) : imageUrl.replace("//", "/");
    return {
      height: `${imageHeight}px`,
      background: `url("${resolved}")  center no-repeat`,
      backgroundSize: "contain",
    };
  }, [imageHeight, imageUrl]);

  const canShowDiscount = richiediDatiVendita && typeof element.cost !== "undefined";

  return (
    <Card
      className="gutter-b card-custom card-stretched flex-fill"
      style={hasInvalidDiscount ? { border: "2px solid #dc3545" } : undefined}
    >
      <Card.Header className={`border-0 py-4 ${element.available ? "" : "text-muted"}`} style={{ minHeight: "45px" }}>
        <h4 style={{ margin: "auto 0" }}>
          {element.name} {element.qta} {element.available ? "" : " - Non disponibile"}
        </h4>
      </Card.Header>

      {imageStyle ? (
        element.pdfUrl ? (
          <a
            href={toAbsoluteUrl(element.pdfUrl)}
            target="_blank"
            rel="noopener noreferrer"
            className="card-img-top mt-0"
          >
            <div className="card-img-top mt-0" style={imageStyle}></div>
          </a>
        ) : (
          <div className="card-img-top mt-0" style={imageStyle}></div>
        )
      ) : null}

      <Card.Body className={`py-4 ${element.available ? "" : "text-muted"}`}>
        <TruncatedTextWithTooltip text={element.description} maxLength={maxDescriptionLength} as={Card.Text} />

        {element.quantity ? (
          <ListGroup className="list-group-flush">
            <ListGroupItem className={`px-0 ${element.available ? "" : "text-muted"}`}>
              {displayName[0].toUpperCase() + displayName.substring(1)} per collo: {element.quantity}
            </ListGroupItem>
            {typeof element.cost !== "undefined" ? (
              <ListGroupItem className={`px-0 ${element.available ? "" : "text-muted"}`}>
                Valore per collo: <CostWithDiscount cost={element.cost} discountPercent={getScontoFinalValue(element.id)} />
              </ListGroupItem>
            ) : null}
          </ListGroup>
        ) : (
          <ListGroup className="list-group-flush">
            {typeof element.cost !== "undefined" ? (
              <ListGroupItem className={`px-0 ${element.available ? "" : "text-muted"}`}>
                Valore: <CostWithDiscount cost={element.cost} discountPercent={getScontoFinalValue(element.id)} />
              </ListGroupItem>
            ) : null}
          </ListGroup>
        )}

        {canShowDiscount ? (
          <ListGroup className="list-group-flush">
            <ListGroupItem className={`px-0 ${element.available ? "" : "text-muted"}`}>
              <Form.Group>
                <InputGroup>
                  <InputGroup.Prepend>
                    <InputGroup.Text
                      id="scontoGroupPrepend"
                      className={hasInvalidDiscount ? "bg-danger text-light" : ""}
                    >
                      Sconto
                    </InputGroup.Text>
                  </InputGroup.Prepend>
                  {(() => {
                    const { options, placeholder, disabled } = getDiscountOptionsForElement(element.division, element.sku);
                    const currentValue = getDiscountFinalValue(getScontoValue(element.id));
                    const selectedOption =
                      Number.isFinite(currentValue) && currentValue > 0 ? options.find((o) => o.value === currentValue) || null : null;

                    const disabledReason =
                      placeholder === "Divisione non valida" || placeholder === "Configurazione sconti non disponibile"
                        ? placeholder
                        : disabled
                        ? "Nessuno sconto disponibile"
                        : null;

                    const selectEl = (
                      <Select
                        inputId={`discount_select_${element.id}`}
                        aria-label={`Sconto per ${element.name}`}
                        options={options}
                        value={selectedOption}
                        placeholder={placeholder}
                        isDisabled={disabled}
                        isClearable
                        menuPlacement="auto"
                        onChange={(opt) => {
                          const newDiscountValue = opt ? Number(opt.value) : 0;
                          if (!Number.isFinite(newDiscountValue) || newDiscountValue < 0 || newDiscountValue > 100) return;
                          onDiscountChange(element.id, newDiscountValue);
                        }}
                      />
                    );

                    if (discountsLoading) {
                      return (
                        <div className="d-flex align-items-center pl-2">
                          <span className="spinner spinner-sm spinner-primary"></span>
                          <span className="ml-2 text-muted">Caricamento…</span>
                        </div>
                      );
                    }

                    return disabledReason ? (
                      <OverlayTrigger placement="top" overlay={<Tooltip id={`discount_tt_${element.id}`}>{disabledReason}</Tooltip>}>
                        <div style={{ flex: 1 }}>{selectEl}</div>
                      </OverlayTrigger>
                    ) : (
                      <div style={{ flex: 1 }}>{selectEl}</div>
                    );
                  })()}
                </InputGroup>
              </Form.Group>
            </ListGroupItem>
          </ListGroup>
        ) : null}
      </Card.Body>

      <Card.Footer className="border-0 py-2">
        <ButtonToolbar className="mb-3 justify-content-between" aria-label="Bottoni">
          <div className="my-auto">
            <DivisionChip division={element.division} className={divisionClassName} />
          </div>
          {element.available ? (
            <InputGroup>
              <Button
                variant="outline"
                className={divisionClassName}
                onClick={() => setCartValue(element.id, quantityInCart - 1)}
              >
                <i className={"ki ki-minus icon-xs p-0 " + divisionClassName}></i>
              </Button>
              <Form.Control
                style={{ width: "60px", height: "auto" }}
                className={divisionClassName}
                type="number"
                value={quantityInCart}
                aria-label="Quantità"
                onChange={(event) => setCartValue(element.id, parseInt(event.target.value, 10))}
              />
              <Button
                variant="outline"
                className={divisionClassName}
                onClick={() => setCartValue(element.id, quantityInCart + 1)}
              >
                <i className={"ki ki-plus icon-xs p-0 " + divisionClassName}></i>
              </Button>
            </InputGroup>
          ) : (
            <p>Non disponibile</p>
          )}
        </ButtonToolbar>
      </Card.Footer>
    </Card>
  );
});

ShopProductCard.propTypes = {
  element: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    name: PropTypes.string.isRequired,
    qta: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    sku: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    description: PropTypes.string,
    division: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    imageUrl: PropTypes.string,
    pdfUrl: PropTypes.string,
    quantity: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    cost: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    available: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  }).isRequired,
  divisionClassName: PropTypes.string,
};
